import { Fragment, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import dayjs from 'dayjs';

import { sortDates, sortNumbers, sortStrings } from '../../helpers/filter';
import { getTableColumns } from '../../helpers/subscription';

import { DATE_FORMAT, EDIT_COMPONENT_TYPE } from '../../constants/common';
import messages from '../../constants/messages';
import { FEATURE_LIST } from '../../constants/subscription';
import { TABLE_CONFIG_COLUMNS } from '../../constants/table-configurations';

import { COLUMN_SIZE } from '../../components/AppTable/constants';

import { setRotationDetail } from '../../redux/reducers/geofence-rotations';

const commonColumnProperties = {
  ellipsis: true,
  sortDirections: ['ascend', 'descend']
};

export const useGrazingColumns = () => {
  const { myPlan } = useSelector(state => state.subscription);
   const dispatch = useDispatch();

  const columns = [
    {
      ...commonColumnProperties,
      title: 'Rotation ID',
      configColumnKey: TABLE_CONFIG_COLUMNS.ID,
      id: 'id',
      fixed: 'left',
      searchable: true,
      width: COLUMN_SIZE.XS,
      render: value =><span className="rg-pointer text-primary color-hover" onClick={() => dispatch(setRotationDetail(value))}>{value.rotation_identifier}</span>,
      sorter: (a, b) => sortStrings(a.rotation_identifier, b.rotation_identifier)
    },
    {
      ...commonColumnProperties,
      title: 'Rotation Name',
      dataIndex: 'rotation_name',
      configColumnKey: TABLE_CONFIG_COLUMNS.NAME,
      editable: true,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
      id: 'name',
      searchable: true,
      width: COLUMN_SIZE.XL,
      sorter: (a, b) => sortStrings(a.rotation_name, b.rotation_name)
    },
    {
      ...commonColumnProperties,
      title: 'Pasture',
      dataIndex: 'pasture',
      configColumnKey: TABLE_CONFIG_COLUMNS.PASTURE_NAME,
      id: 'pasture',
      fixed: 'left',
      searchable: true,
      width: COLUMN_SIZE.LG,
      render: value => <Link to={`/pastures/${value.id}`}>{value.name}</Link>,
      sorter: (a, b) => sortStrings(a.pasture.name, b.pasture.name)
    },
    {
      ...commonColumnProperties,
      title: 'Assigned Groups',
      dataIndex: 'assigned_groups',
      configColumnKey: TABLE_CONFIG_COLUMNS.ASSIGNED_GROUPS,
      id: 'assigned_groups',
      searchable: true,
      width: COLUMN_SIZE.XXL,
      render: value =>
        value?.length
          ? value.map((v, i) => (
              <Fragment key={v.label_id}>
                <Link to={`/group/${v.label_id}`}>{v.label_name}</Link>
                {i < value.length - 1 && ', '}
              </Fragment>
            ))
          : null
    },
    {
      ...commonColumnProperties,
      title: 'Expected Tracked Livestock',
      dataIndex: 'expected_tracked_livestock',
      configColumnKey: TABLE_CONFIG_COLUMNS.EXPECTED_TRACKED_LIVESTOCK,
      id: 'expected_tracked_livestock',
      searchable: true,
      width: COLUMN_SIZE.SM,
      sorter: (a, b) => sortNumbers(a.expected_tracked_livestock, b.expected_tracked_livestock)
    },
    {
      ...commonColumnProperties,
      title: 'Actual Tracked Livestock',
      dataIndex: 'actual_tracked_livestock',
      configColumnKey: TABLE_CONFIG_COLUMNS.ACTUAL_TRACKED_LIVESTOCK,
      id: 'actual_tracked_livestock',
      searchable: true,
      width: COLUMN_SIZE.SM,
      sorter: (a, b) => sortNumbers(a.actual_tracked_livestock, b.actual_tracked_livestock)
    },
    {
      ...commonColumnProperties,
      title: 'Forage species',
      dataIndex: 'forage_species',
      configColumnKey: TABLE_CONFIG_COLUMNS.FORAGE_SPECIES,
      editable: true,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
      id: 'forage_species',
      searchable: true,
      width: COLUMN_SIZE.XXL,
      render: value => value?.join(', '),
      onFilter: (value, record) => JSON.stringify(record.forage_species).toLowerCase().includes(value.toLowerCase()),
      sorter: (a, b) => sortStrings(JSON.stringify(a.forage_species), JSON.stringify(b.forage_species))
    },
    {
      ...commonColumnProperties,
      title: 'Start Date',
      dataIndex: 'rotation_start_date',
      configColumnKey: TABLE_CONFIG_COLUMNS.START_DATE,
      editable: true,
      editConfig: { type: EDIT_COMPONENT_TYPE.DATE_PICKER },
      id: 'rotation_start_date',
      searchable: true,
      width: COLUMN_SIZE.LG,
      onFilter: (value, record) => {
        const date = record.rotation_start_date ? dayjs(record.rotation_start_date).format(DATE_FORMAT.DATE) : '';

        return date.includes(value);
      },
      render: value => dayjs(value).format(DATE_FORMAT.DATE),
      sorter: (a, b) => sortDates(a.rotation_start_date, b.rotation_start_date)
    },
    {
      ...commonColumnProperties,
      title: 'End Date',
      dataIndex: 'rotation_end_date',
      configColumnKey: TABLE_CONFIG_COLUMNS.END_DATE,
      editable: true,
      editConfig: { type: EDIT_COMPONENT_TYPE.DATE_PICKER },
      id: 'rotation_end_date',
      searchable: true,
      width: COLUMN_SIZE.LG,
      onFilter: (value, record) => {
        const date = record.rotation_end_date ? dayjs(record.rotation_end_date).format(DATE_FORMAT.DATE) : '';

        return date.includes(value);
      },
      render: value => dayjs(value).format(DATE_FORMAT.DATE),
      sorter: (a, b) => sortDates(a.rotation_end_date, b.rotation_end_date)
    },
    {
      ...commonColumnProperties,
      title: 'Duration',
      dataIndex: 'duration',
      configColumnKey: TABLE_CONFIG_COLUMNS.DURATION,
      editable: true,
      id: 'duration',
      searchable: true,
      width: COLUMN_SIZE.LG,
      onFilter: (value, record) => {
        const duration = record
          ? `${dayjs(record?.rotation_end_date)?.diff(dayjs(record?.rotation_start_date), 'day')}`
          : null;

        return String(duration).includes(value);
      },
      render: (value, record) => `${dayjs(record?.rotation_end_date)?.diff(dayjs(record?.rotation_start_date), 'day')}`,
      sorter: (a, b) => {
        const strA = a ? `${dayjs(a?.rotation_end_date)?.diff(dayjs(a?.rotation_start_date), 'day')}` : null;
        const strB = b ? `${dayjs(b?.rotation_end_date)?.diff(dayjs(b?.rotation_start_date), 'day')}` : null;

        return sortDates(strA, strB);
      }
    },
    {
      ...commonColumnProperties,
      title: 'Stocking Rate',
      dataIndex: 'rotation_stocking_rate',
      configColumnKey: TABLE_CONFIG_COLUMNS.STOCKING_RATE,
      id: 'rotation_stocking_rate',
      searchable: true,
      width: COLUMN_SIZE.LG,
      sorter: (a, b) => sortNumbers(a?.rotation_stocking_rate, b?.rotation_stocking_rate)
    },
    {
      ...commonColumnProperties,
      title: 'Days Grazed',
      dataIndex: 'rotation_lsu_days_grazing',
      configColumnKey: TABLE_CONFIG_COLUMNS.LSU_DAYS_GRAZING,
      id: 'rotation_lsu_days_grazing',
      searchable: true,
      width: COLUMN_SIZE.LG,
      sorter: (a, b) => sortNumbers(a?.rotation_lsu_days_grazing, b?.rotation_lsu_days_grazing)
    },
    {
      ...commonColumnProperties,
      title: 'Rest since last rotation',
      dataIndex: 'rest_since_last_rotation',
      configColumnKey: TABLE_CONFIG_COLUMNS.REST_SINCE_LAST_ROTATION,
      id: 'rest_since_last_rotation',
      searchable: true,
      width: COLUMN_SIZE.XL,
      onFilter: (value, record) => {
        const days = record.lastRotation?.rotation_end_date
          ? `${dayjs()?.diff(dayjs(record.lastRotation?.rotation_end_date), 'day')}`
          : null;

        return String(days).includes(value);
      },
      render: (value, record) =>
        record.lastRotation?.rotation_end_date
          ? `${dayjs()?.diff(dayjs(record.lastRotation?.rotation_end_date), 'day')}`
          : null,
      sorter: (a, b) => {
        const strA = a.lastRotation?.rotation_end_date
          ? `${dayjs()?.diff(dayjs(a.lastRotation?.rotation_end_date), 'day')}`
          : null;
        const strB = b.lastRotation?.rotation_end_date
          ? `${dayjs()?.diff(dayjs(b.lastRotation?.rotation_end_date), 'day')}`
          : null;

        return sortNumbers(strA, strB);
      }
    },
    {
      ...commonColumnProperties,
      title: 'Rest until next rotation',
      dataIndex: 'rest_until_next_rotation',
      configColumnKey: TABLE_CONFIG_COLUMNS.REST_UNTIL_NEXT_ROTATION,
      id: 'rest_until_next_rotation',
      searchable: true,
      width: COLUMN_SIZE.XL,
      onFilter: (value, record) => {
        const days = record.nextRotation?.rotation_start_date
          ? `${dayjs()?.diff(dayjs(record.nextRotation?.rotation_start_date), 'day')}`
          : null;

        return String(days).includes(value);
      },
      render: (value, record) =>
        record.nextRotation?.rotation_start_date
          ? `${dayjs(record.nextRotation?.rotation_start_date)?.diff(dayjs(), 'day')}`
          : null,
      sorter: (a, b) => {
        const strA = a.nextRotation?.rotation_start_date
          ? `${dayjs(a.nextRotation?.rotation_start_date)?.diff(dayjs(), 'day')}`
          : null;
        const strB = b.nextRotation?.rotation_start_date
          ? `${dayjs(b.nextRotation?.rotation_start_date)?.diff(dayjs(), 'day')}`
          : null;

        return sortNumbers(strA, strB);
      }
    },
    {
      ...commonColumnProperties,
      title: 'Forage cover before',
      dataIndex: 'forage_cover_before_rotation',
      id: 'forage_cover_before_rotation',
      configColumnKey: TABLE_CONFIG_COLUMNS.FORAGE_COVER_BEFORE_ROTATION,
      editable: true,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT_NUMBER },
      rules: [{ type: 'number', message: messages.FIELD_INVALID('Forage cover') }],
      searchable: true,
      width: COLUMN_SIZE.XL,
      onFilter: (value, record) => String(record?.forage_cover_before_rotation).includes(value),
      sorter: (a, b) => sortNumbers(a.forage_cover_before_rotation, b.forage_cover_before_rotation)
    },
    {
      ...commonColumnProperties,
      title: 'Forage cover after',
      dataIndex: 'forage_cover_after_rotation',
      id: 'forage_cover_after_rotation',
      configColumnKey: TABLE_CONFIG_COLUMNS.FORAGE_COVER_AFTER_ROTATION,
      editable: true,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT_NUMBER },
      rules: [{ type: 'number', message: messages.FIELD_INVALID('Forage cover') }],
      searchable: true,
      width: COLUMN_SIZE.XL,
      onFilter: (value, record) => String(record?.forage_cover_after_rotation).includes(value),
      sorter: (a, b) => sortNumbers(a.forage_cover_after_rotation, b.forage_cover_after_rotation)
    },
    {
      ...commonColumnProperties,
      title: 'Forage height before',
      dataIndex: 'forage_height_before_rotation',
      id: 'forage_height_before_rotation',
      configColumnKey: TABLE_CONFIG_COLUMNS.FORAGE_HEIGHT_BEFORE_ROTATION,
      editable: true,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT_NUMBER },
      rules: [{ type: 'number', message: messages.FIELD_INVALID('Forage height') }],
      searchable: true,
      width: COLUMN_SIZE.XL,
      onFilter: (value, record) => String(record.forage_height_before_rotation).includes(value),
      render: (value, record) => record.forage_height_before_rotation,
      sorter: (a, b) => sortNumbers(a.forage_height_before_rotation, b.forage_height_before_rotation)
    },
    {
      ...commonColumnProperties,
      title: 'Forage height after',
      dataIndex: 'forage_height_after_rotation',
      id: 'forage_height_after_rotation',
      configColumnKey: TABLE_CONFIG_COLUMNS.FORAGE_HEIGHT_AFTER_ROTATION,
      editable: true,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT_NUMBER },
      rules: [{ type: 'number', message: messages.FIELD_INVALID('Forage height') }],
      searchable: true,
      width: COLUMN_SIZE.XL,
      onFilter: (value, record) => String(record.forage_height_after_rotation).includes(value),
      render: (value, record) => record.forage_height_after_rotation,
      sorter: (a, b) => sortNumbers(a.forage_height_after_rotation, b.forage_height_after_rotation)
    },
    {
      ...commonColumnProperties,
      title: 'Biomass before',
      dataIndex: 'biomass_weight_before_rotation',
      id: 'biomass_weight_before_rotation',
      configColumnKey: TABLE_CONFIG_COLUMNS.BIOMASS_WEIGHT_BEFORE_ROTATION,
      editable: true,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT_NUMBER },
      rules: [{ type: 'number', message: messages.FIELD_INVALID('Biomass weight') }],
      searchable: true,
      width: COLUMN_SIZE.LG,
      onFilter: (value, record) => String(record.biomass_weight_before_rotation).includes(value),
      render: (value, record) => record.biomass_weight_before_rotation,
      sorter: (a, b) => sortNumbers(a.biomass_weight_before_rotation, b.biomass_weight_before_rotation)
    },
    {
      ...commonColumnProperties,
      title: 'Biomass after',
      dataIndex: 'biomass_weight_after_rotation',
      id: 'biomass_weight_after_rotation',
      configColumnKey: TABLE_CONFIG_COLUMNS.BIOMASS_WEIGHT_AFTER_ROTATION,
      editable: true,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT_NUMBER },
      rules: [{ type: 'number', message: messages.FIELD_INVALID('Biomass weight') }],
      searchable: true,
      width: COLUMN_SIZE.LG,
      onFilter: (value, record) => String(record.biomass_weight_after_rotation).includes(value),
      render: (value, record) => record.biomass_weight_after_rotation,
      sorter: (a, b) => sortNumbers(a.biomass_weight_after_rotation, b.biomass_weight_after_rotation)
    }
  ];

  return useMemo(() => getTableColumns(columns, myPlan?.type, FEATURE_LIST.GRAZING_LIST), [myPlan?.type]);
};
